// RowsView: Zeigt Karten in Kategorien als Zeilen an mit Drag & Drop
import React, { useMemo, useCallback, useRef, useEffect, useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import UiCardImage from '../../../ui/CardImage';
import CardOverlays from '../../../ui/CardOverlays';
import { useCardPeek } from '../../../ui/useCardPeek';
import { Minus, Plus } from 'lucide-react';
import CategoryHeader from '../../../ui/CategoryHeader';
import ConfirmDialog from '../../../ui/ConfirmDialog';
import { fittingColumnCount } from '../utils/columnFit';
import { formatPrice } from '../utils/formatPrice';
import { useDeckCardActions } from '../hooks/useDeckCardActions';
import { useDeckViewState } from '../hooks/useDeckViewState';
import { useShouldBeTransparent } from '../hooks/useShouldBeTransparent';
import { useDeckDragHandlers } from '../hooks/useDeckDragHandlers';
import CardContextMenu from '../components/CardContextMenu.js';
import LabelIndicators from '../components/LabelIndicators.js';
import '../../../components/CardViews.css';

// ========== KONFIGURATIONSKONSTANTEN ==========
// Diese Konstanten können angepasst werden um das Layout zu verändern

// MTG-Karten Seitenverhältnis — lokal statt Import aus shared/CardBase: CardInRow
// rendert nicht über CardBase, braucht die Konstante aber für die
// Wrapper-Höhenberechnung.
const CARD_ASPECT_RATIO = 680 / 488;

// Karten-Darstellung
const CARD_MIN_WIDTH = 50;           // Minimale Kartenbreite in Pixel
const CARD_MAX_WIDTH = 600;           // Maximale Kartenbreite in Pixel
const CARD_GAP = 5;                   // Abstand zwischen Karten in Pixel
const COLUMN_SAFETY_MARGIN_PX = 4;    // kleiner Puffer gegen Wrap-Flicker (Rundung/Zoom)

// Kategorie-Container
const CATEGORY_PADDING_X = 2;         // Horizontaler Innenabstand der Kategorie-Box
const CATEGORY_PADDING_Y = 8;         // Vertikaler Innenabstand der Kategorie-Box
const CATEGORY_MARGIN_BOTTOM = 12;     // Abstand zwischen Kategorien in Pixel
const CATEGORY_BORDER_RADIUS = 6;     // Rundung der Kategorie-Ecken in Pixel
const CATEGORY_BORDER_PX = 2;         // Border-Breite der Kategorie-Box (für exakte Breitenberechnung)

// Hauptcontainer
const VIEW_PADDING_X = 0;             // Extra Abstand zum Seitenrand innerhalb der RowsView
const VIEW_PADDING_Y = 3;             // Extra Abstand oben/unten innerhalb der RowsView

// ========== KARTEN-KOMPONENTE ==========

// Einzelne Karte in Zeilen-Ansicht - jetzt mit dnd-kit
function CardInRow({ card, cardWidth, canEditQuantity = false, onQuantityDelta, isOwnProfile = false, onSetAsCommander, onShowDetails, onSwitchPrint, highlightedNewCardId, deckLabels, cardLabels, onAssignLabel, onRemoveLabel, onNewLabelRequest }) {
  const cardHeight = cardWidth * CARD_ASPECT_RATIO;
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `card-${card.id}`,
    data: { card },
  });
  // Querformat-Karten: der Wrapper ist zugleich das Bild (kein Info-Feld darunter).
  const peekRef = useRef(null);
  const { peek, handlers: peekHandlers } = useCardPeek({ imageRef: peekRef });
  const setRefs = useCallback((el) => { setNodeRef(el); peekRef.current = el; }, [setNodeRef]);

  const handleSetCommander = useCallback(() => {
    if (onSetAsCommander) {
      onSetAsCommander(card.id, true);
    }
  }, [card.id, onSetAsCommander]);

  // Zwei unabhängige Sichtbarkeits-Bedingungen (heutiges Verhalten 1:1 erhalten):
  // Mengen-Buttons brauchen Edit-Recht, das ⋮-Menü ist auch bei reinem Lese-Zugriff
  // (isOwnProfile bei physischem Deck, canEditQuantity=false) verfügbar — z.B. um
  // "Als Commander setzen" auszulösen. Beides zusammen zu verschmelzen würde diesen
  // Fall regressen.
  const canEditQty = isOwnProfile && canEditQuantity && typeof onQuantityDelta === 'function' && !isDragging;
  // „Details" steht auch ohne Bearbeitungsrecht offen (fremdes Deck) — deshalb öffnet das
  // Menü dort ebenfalls; alle mutierenden Einträge bleiben unten an isOwnProfile gebunden.
  const canShowMenu = (isOwnProfile || (typeof onShowDetails === 'function' && !!card.oracleId)) && !isDragging;

  const handleQuantityDelta = useCallback((delta) => {
    const id = card.scryfallId || card.id || card['Scryfall ID'];
    if (id) onQuantityDelta(id, delta);
  }, [card, onQuantityDelta]);

  return (
    <div
      ref={setRefs}
      {...listeners}
      {...attributes}
      {...peekHandlers}
      data-card-id={card.id || card.scryfallId}
      className={`card-in-row ${isDragging ? 'is-dragging' : ''}`}
      style={{
        width: cardWidth,
        height: cardHeight,
        opacity: isDragging ? 0 : 1,
        zIndex: isDragging ? 200 : 1,
      }}
    >
      <LabelIndicators labels={cardLabels} variant="tabs" />
      <UiCardImage
        src={card.imageUrl}
        backSrc={card.imageUrlBack}
        alt={card.name}
        landscape="peek"
        peek={peek}
        orientation={card.imageOrientation}
        onOpen={onShowDetails && card.oracleId ? () => onShowDetails(card) : undefined}
        openLabel={`${card.name} — Details`}
      >
        <CardOverlays>
          <CardOverlays.Corner position="top-left">
            {(card.quantity || 0) > 1 && (
              <CardOverlays.Badge>×{card.quantity}</CardOverlays.Badge>
            )}
            {Number(card.price) > 0 && (
              <CardOverlays.Badge hover>{formatPrice(card.price)}</CardOverlays.Badge>
            )}
          </CardOverlays.Corner>
          {(canEditQty || canShowMenu) && (
            <CardOverlays.Actions>
              {canEditQty && (
                <>
                  <CardOverlays.Button icon={Minus} label="Eine Kopie entfernen" onClick={() => handleQuantityDelta(-1)} />
                  <CardOverlays.Button icon={Plus} label="Eine Kopie hinzufügen" onClick={() => handleQuantityDelta(1)} />
                </>
              )}
              {canShowMenu && (
                <CardContextMenu
                  overlayTrigger
                  card={card}
                  onShowDetails={onShowDetails && card.oracleId ? onShowDetails : undefined}
                  onSwitchPrint={onSwitchPrint && card.oracleId ? onSwitchPrint : undefined}
                  onSetCommander={isOwnProfile ? handleSetCommander : undefined}
                  deckLabels={deckLabels}
                  cardLabels={cardLabels}
                  onAssignLabel={(label) => onAssignLabel(card.deckCardEntryId, label)}
                  onRemoveLabel={(labelId) => onRemoveLabel(card.deckCardEntryId, labelId)}
                  onNewLabel={isOwnProfile ? () => onNewLabelRequest(card.deckCardEntryId) : undefined}
                />
              )}
            </CardOverlays.Actions>
          )}
          <CardOverlays.HighlightRing active={(card.id || card.scryfallId) === highlightedNewCardId} />
        </CardOverlays>
      </UiCardImage>
    </div>
  );
}

// ========== KATEGORIE-KOMPONENTE ==========

// Eine Zeile von Karten (Kategorie) - jetzt mit dnd-kit
function CategoryRow({ category, categoryId, excluded, cards, isDropTarget, cardWidth, canEditQuantity = false, onQuantityDelta, isOwnProfile, onSetAsCommander, onShowDetails, onSwitchPrint, onRemoveCategory, canRemove, isDraggingCategory, isDroppable, externalDragging = false, externalDropProps, stickyTop = 0, highlightedNewCardId, deckLabels, getCardLabels, onAssignLabel, onRemoveLabel, onNewLabelRequest, strategy, renameCategory, onCategoryRenamed }) {
  // Cards sind vom Parent bereits gefiltert
  const visibleCards = cards;

  const totalCards = useMemo(() => 
    visibleCards.reduce((sum, c) => sum + (c.quantity || 1), 0),
    [visibleCards]
  );
  const totalPrice = useMemo(
    () => visibleCards.reduce((sum, c) => sum + ((Number(c.price) || 0) * (Number(c.quantity) || 0)), 0),
    [visibleCards]
  );
  
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `category-${category}`,
    data: { category, categoryId },
    disabled: !isDroppable,
  });
  const { setNodeRef: setSortRef, attributes, listeners, transform, transition, isDragging } = useSortable({ id: `cat-${category}` });
  const setCombinedRef = (el) => {
    setDropRef(el);
    setSortRef(el);
  };
  const styleTransform = transform
    ? CSS.Transform.toString({
      ...transform,
      scaleX: 1,
      scaleY: 1,
    })
    : undefined;
  // Nur als Drop-Target hervorheben wenn eine Karte gedraggt wird, nicht wenn eine Kategorie gedraggt wird
  const showAsDropTarget = !isDraggingCategory && (isDropTarget || isOver);
  
  const handleRemoveClick = useCallback(() => {
    if (onRemoveCategory) {
      onRemoveCategory(category);
    }
  }, [category, onRemoveCategory]);

  // Rename nur für echte, persistierte Kategorien des eigenen Decks
  // (nicht 'other'/Nicht-Kategorie-Gruppierungen, nicht Commander — der hat keinen Header hier).
  const canRenameCategory = Boolean(isOwnProfile && strategy?.persistent && categoryId != null);

  const handleRenameCategory = useCallback(async (nextName) => {
    const result = await renameCategory?.(categoryId, nextName);
    onCategoryRenamed?.(category, result);
  }, [categoryId, category, renameCategory, onCategoryRenamed]);

  return (
    <div
      ref={setCombinedRef}
      {...attributes}
      data-drop-group={category}
      data-external-target={isDropTarget && externalDragging ? 'true' : undefined}
      {...externalDropProps}
      className="card-overlays-scope"
      style={{
        width: '100%',
        padding: `${CATEGORY_PADDING_Y}px ${CATEGORY_PADDING_X}px`,
        borderRadius: `${CATEGORY_BORDER_RADIUS}px`,
        // Die Box trägt im Ruhezustand keine eigene Fläche (transparent) — der
        // Kategorie-Header ist die einzige feste Fläche, der Inhalt darunter
        // liegt direkt auf der Matte. Nur der Drop-Ziel-Zustand bleibt sichtbar
        // gefärbt. Excluded-Kennzeichnung liefert allein der ui/CategoryHeader
        // (gestrichelter Rahmen + "Nicht im Deck"-Badge) — kein zusätzlicher
        // Voll-Container-Rahmen.
        backgroundColor: showAsDropTarget ? 'var(--accent-tint)' : 'transparent',
        border: showAsDropTarget
          ? '2px dashed var(--accent)'
          : externalDragging
            ? '2px dashed var(--accent-hint)'
            : '2px solid transparent',
        transition,
        marginBottom: `${CATEGORY_MARGIN_BOTTOM}px`,
        boxSizing: 'border-box',
        transform: styleTransform,
        opacity: isDragging ? 0.4 : 1,
      }}
    >
      {/* Kategorie-Header - komplett dragbar für Kategorie-Sortierung */}
      <CategoryHeader
        name={category}
        count={totalCards}
        price={formatPrice(totalPrice)}
        excluded={excluded}
        dragHandle={isOwnProfile}
        dragListeners={isOwnProfile ? listeners : undefined}
        isDropTarget={showAsDropTarget}
        stickyTop={stickyTop}
        canRename={canRenameCategory}
        onRename={(next) => handleRenameCategory(next)}
        canRemove={canRemove && isOwnProfile}
        onRemove={handleRemoveClick}
      />

      {/* Karten in Zeile */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: `${CARD_GAP}px`,
          alignItems: 'flex-start',
          // Ohne diesen Wert läge der Kategorie-Header direkt auf den Karten.
          marginTop: '10px',
        }}
      >
        {visibleCards.map((card, idx) => (
          <CardInRow
            key={`${card.id}-${idx}`}
            card={card}
            cardWidth={cardWidth}
            canEditQuantity={canEditQuantity}
            onQuantityDelta={onQuantityDelta}
            isOwnProfile={isOwnProfile}
            onSetAsCommander={onSetAsCommander}
            onShowDetails={onShowDetails}
            onSwitchPrint={onSwitchPrint}
            highlightedNewCardId={highlightedNewCardId}
            deckLabels={deckLabels}
            cardLabels={getCardLabels(card.deckCardEntryId)}
            onAssignLabel={onAssignLabel}
            onRemoveLabel={onRemoveLabel}
            onNewLabelRequest={onNewLabelRequest}
          />
        ))}
        {/* Drop-Animations-Placeholder: width=cardWidth → korrektes Wrap-
            Verhalten als nächster Slot, height=4 → minimaler Wrap-Overhead.
            align-self defensiv falls Container später auf stretch geht. */}
        <div
          className="card-drop-placeholder card-drop-placeholder--rows"
          data-placeholder-category={category}
          style={{
            width: cardWidth,
            height: 4,
            alignSelf: 'flex-start',
          }}
        />
      </div>
    </div>
  );
}

// ========== HAUPTKOMPONENTE ==========

// ========== HAUPTKOMPONENTE ==========

export default function RowsView({
  externalDrop,
  deck,
  isOwnProfile,
  onCategoryChange,
  canEditQuantity = false,
  onQuantityDelta,
  columnCount = 6,
  sortBy = 'name',
  searchTerm = '',
  highlightedCmc = null,
  strategy,
  matchingCardIds = null,
  isDraggingCategory = false,
  onRegisterDndHandlers,
  showExcluded = true,
  onRemoveCardsByCategory,
  onShowDetails,
  onSwitchPrint,
  stickyTop = 0,
  highlightedNewCardId,
  deckLabels,
  getCardLabels,
  onAssignLabel,
  onRemoveLabel,
  onNewLabelRequest,
  onDropError,
  onCategoryRenamed,
  onCardMoved,
  onCatalogCardDrop,
}) {
  const { authToken } = useAuth();

  // Hook für Deck-Karten-Aktionen (setAsCommander)
  const { setAsCommander } = useDeckCardActions({
    deck,
    canEdit: isOwnProfile,
    onError: onDropError,
  });

  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  // Berechne Container-Breite für Spaltenberechnung
  useEffect(() => {
    if (!containerRef.current) return;

    const updateWidth = () => {
      if (!containerRef.current) return;
      setContainerWidth(containerRef.current.clientWidth);
    };

    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, []);

  // Was einmalig von der Breite abgeht: Rand der Ansicht, Rahmen und Innenabstand des
  // Kategorie-Kastens, Puffer gegen Wrap-Flicker.
  const fixedExtra = VIEW_PADDING_X * 2 + (CATEGORY_PADDING_X + CATEGORY_BORDER_PX) * 2 + COLUMN_SAFETY_MARGIN_PX;

  // Tatsächlich gerenderte Spaltenzahl (Kappung siehe utils/columnFit.js).
  const effectiveColumnCount = useMemo(() => fittingColumnCount({
    containerWidth,
    columnCount,
    minCardWidth: CARD_MIN_WIDTH,
    gap: CARD_GAP,
    fixedExtra,
  }), [containerWidth, columnCount, fixedExtra]);

  // Berechne Kartenbreite basierend auf Spaltenanzahl und Container-Breite
  const cardWidth = useMemo(() => {
    if (containerWidth === 0) return CARD_MIN_WIDTH;
    const totalGaps = (effectiveColumnCount - 1) * CARD_GAP;
    const availableWidth = containerWidth - fixedExtra - totalGaps;
    const calculated = Math.floor(availableWidth / effectiveColumnCount);
    const clamped = Math.max(CARD_MIN_WIDTH, Math.min(CARD_MAX_WIDTH, calculated));
    return clamped;
  }, [containerWidth, effectiveColumnCount, fixedExtra]);

  // Gemeinsame Hooks für View-State, Transparenz und DnD
  const { groups, categories, moveCategory, removeCategory, addCategory, renameCategory, excludedCategoryNames } = useDeckViewState({
    deck, sortBy, strategy, showExcluded, isOwnProfile, authToken,
  });

  const shouldBeTransparent = useShouldBeTransparent({ highlightedCmc, searchTerm, matchingCardIds });
  const isFilterActive = !!(searchTerm?.trim() || highlightedCmc !== null || matchingCardIds !== null);

  const { dropTarget, handleRemoveCategory, handleSetAsCommander, pendingDeleteCategory, confirmDeleteCategory, cancelDeleteCategory, applyManualOrder } = useDeckDragHandlers({
    deck, isOwnProfile, onCategoryChange, categories,
    moveCategory, removeCategory, addCategory, sortBy, setAsCommander,
    onRemoveCardsByCategory, onRegisterDndHandlers, cardWidth,
    strategy, excludedCategoryNames, onDropError, onCardMoved, onCatalogCardDrop,
  });

  const groupsFiltered = useMemo(() => {
    return groups.map(g => {
      const ordered = applyManualOrder(g.name, g.cards);
      const cards = isFilterActive ? ordered.filter(card => !shouldBeTransparent(card)) : ordered;
      return { ...g, cards };
    });
  }, [groups, applyManualOrder, isFilterActive, shouldBeTransparent]);
  
  if (!deck) {
    return <div style={{ color: 'var(--text-secondary)', padding: '20px' }}>Kein Deck geladen</div>;
  }
  
  return (<>
      <div
        ref={containerRef}
        style={{
          padding: `${VIEW_PADDING_Y}px ${VIEW_PADDING_X}px`,
          boxSizing: 'border-box',
          overflowX: 'clip',
        }}
      >
        {/* Rows-Ansicht mit Sortable Kategorien */}
        <SortableContext items={groupsFiltered.map(g => `cat-${g.name}`)} strategy={rectSortingStrategy}>
          {groupsFiltered.map((group) => {
            const externesZiel = externalDrop && group.id != null && !group.isCommander;
            return (
            <CategoryRow
              key={group.name}
              category={group.name}
              categoryId={group.id}
              excluded={!!(group.excluded || group.type === 'excluded')}
              cards={group.cards}
              isDropTarget={dropTarget === group.name || (externesZiel && externalDrop.target === group.name)}
              externalDragging={Boolean(externesZiel && externalDrop.active)}
              externalDropProps={externesZiel ? externalDrop.propsFor(group.name, group.id) : undefined}
              cardWidth={cardWidth}
              canEditQuantity={canEditQuantity}
              onQuantityDelta={onQuantityDelta}
              isOwnProfile={isOwnProfile}
              onSetAsCommander={handleSetAsCommander}
              onShowDetails={onShowDetails}
              onSwitchPrint={onSwitchPrint}
              onRemoveCategory={handleRemoveCategory}
              canRemove={strategy.editable}
              isDroppable={strategy.editable || !!group.excluded || group.type === 'excluded'}
              isDraggingCategory={isDraggingCategory}
              stickyTop={stickyTop}
              highlightedNewCardId={highlightedNewCardId}
              deckLabels={deckLabels}
              getCardLabels={getCardLabels}
              onAssignLabel={onAssignLabel}
              onRemoveLabel={onRemoveLabel}
              onNewLabelRequest={onNewLabelRequest}
              strategy={strategy}
              renameCategory={renameCategory}
              onCategoryRenamed={onCategoryRenamed}
            />
            );
          })}
        </SortableContext>
      </div>
      <ConfirmDialog
        open={!!pendingDeleteCategory}
        onOpenChange={(next) => { if (!next) cancelDeleteCategory(); }}
        onConfirm={confirmDeleteCategory}
        title="Kategorie löschen"
        description="Wenn du diese Kategorie löschst, werden alle enthaltenen Karten aus dem Deck entfernt."
        confirmLabel="Löschen"
        tone="danger"
      />
  </>);
}
